// Bottom-sheet feedback form. Two required 1-5 star ratings (How much do
// you like HanZi World / How likely will you continue) plus an optional text
// note. Submits a typed POST to /api/feedback; the server writes one row
// to the dedicated `feedback` table (migration 0002). user_id and email
// are attached when the user is signed in; anonymous submissions write
// null to user_id (column is nullable on the table).

import { useEffect, useRef, useState } from 'react';
import { Send, Star, X } from 'lucide-react';
import { getAuth } from '@/lib/auth/auth';
import { C } from '@/styles/tokens';

interface FeedbackSheetProps {
  onClose: () => void;
  /** Fired after a successful submit so the parent can show a toast. */
  onSubmitted: () => void;
}

const MAX_LEN = 1000;
const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? '/api';

type Rating = 1 | 2 | 3 | 4 | 5 | 0;

export function FeedbackSheet({ onClose, onSubmitted }: FeedbackSheetProps) {
  const [likeRating, setLikeRating] = useState<Rating>(0);
  const [continueRating, setContinueRating] = useState<Rating>(0);
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const firstStarRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Open with focus on the first rating so keyboard users can drive the
  // whole form without reaching for the mouse.
  useEffect(() => {
    const id = window.setTimeout(() => firstStarRef.current?.focus(), 80);
    return () => window.clearTimeout(id);
  }, []);

  const trimmed = message.trim();
  const ratingsSet = likeRating > 0 && continueRating > 0;
  const canSubmit = ratingsSet && !submitting;

  async function handleSubmit() {
    if (!canSubmit) return;
    setError(null);
    setSubmitting(true);
    try {
      const jwt = getAuth().current()?.jwt;
      const headers: Record<string, string> = { 'content-type': 'application/json' };
      if (jwt) headers.authorization = `Bearer ${jwt}`;
      const res = await fetch(`${API_BASE}/feedback`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({
          likeRating,
          continueRating,
          ...(trimmed.length > 0 ? { message: trimmed } : {}),
        }),
      });
      if (!res.ok) {
        const detail = await res.text().catch(() => '');
        throw new Error(detail || `Request failed (${res.status})`);
      }
      onSubmitted();
      onClose();
    } catch (err) {
      setError((err as Error).message || 'Could not send feedback.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div role="dialog" aria-modal aria-label="Send feedback" className="fixed inset-0 z-50 flex flex-col">
      <div
        className="absolute inset-0 anim-backdrop-in"
        style={{ background: 'rgba(26,22,20,0.45)' }}
        aria-hidden
        onClick={onClose}
      />
      <div
        className="relative mt-auto flex flex-col rounded-t-[28px] overflow-hidden anim-sheet-up"
        style={{ background: C.cream, boxShadow: '0 -10px 30px rgba(26,22,20,0.22)', maxHeight: '90vh' }}
      >
        <span
          aria-hidden
          className="absolute top-2 left-1/2 w-10 h-1 rounded-full"
          style={{ transform: 'translateX(-50%)', background: 'rgba(26,22,20,0.18)' }}
        />

        <header
          className="pt-6 pb-3 px-5 flex items-center justify-between"
          style={{ borderBottom: '1px solid rgba(0,0,0,0.06)' }}
        >
          <div className="ff-display italic text-2xl flex items-baseline gap-2" style={{ color: C.ink }}>
            Send feedback
            <span className="ff-zh-serif not-italic text-base opacity-50">· 反馈</span>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close feedback"
            className="w-9 h-9 rounded-full flex items-center justify-center"
            style={{ background: 'white' }}
          >
            <X size={16} aria-hidden />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
          <RatingQuestion
            label="How much do you like HanZi World?"
            hz="你有多喜欢"
            value={likeRating}
            onChange={setLikeRating}
            firstStarRef={firstStarRef}
          />
          <RatingQuestion
            label="How likely will you continue using HanZi World?"
            hz="是否会继续使用"
            value={continueRating}
            onChange={setContinueRating}
          />

          <div>
            <label
              htmlFor="feedback-message"
              className="ff-body text-[10px] uppercase tracking-[0.25em] mb-2 block"
              style={{ color: C.inkLight }}
            >
              Anything else? <span style={{ color: C.inkSofter, fontSize: 9 }}>(optional)</span>
            </label>
            <textarea
              id="feedback-message"
              value={message}
              onChange={(e) => setMessage(e.target.value.slice(0, MAX_LEN))}
              placeholder="What's working, what's broken, what you wish existed…"
              className="w-full px-3.5 py-3 rounded-2xl border ff-body text-[13px] leading-relaxed resize-none outline-none transition-all"
              rows={4}
              style={{
                background: 'white',
                borderColor: message ? C.vermillion : 'rgba(0,0,0,0.08)',
                color: C.ink,
                minHeight: 110,
              }}
            />
            <div className="ff-body text-[10px] mt-1.5 text-right" style={{ color: C.inkSofter }}>
              {trimmed.length}/{MAX_LEN}
            </div>
          </div>

          {error && (
            <div
              className="p-3 rounded-2xl ff-body text-[12px]"
              role="alert"
              style={{ background: C.blush, color: C.vermillionDeep }}
            >
              {error}
            </div>
          )}
        </div>

        <div
          className="px-5 pb-7 pt-3 safe-bottom"
          style={{ borderTop: '1px solid rgba(0,0,0,0.06)', background: 'white' }}
        >
          <button
            type="button"
            onClick={() => void handleSubmit()}
            disabled={!canSubmit}
            className="w-full py-3 rounded-full ff-body text-[12px] font-bold uppercase tracking-[0.2em] inline-flex items-center justify-center gap-2 transition-transform active:scale-[0.98] disabled:cursor-not-allowed"
            style={{
              background: canSubmit ? C.vermillion : C.inkSofter,
              color: 'white',
            }}
          >
            <Send size={14} aria-hidden /> {submitting ? 'Sending…' : 'Send'}
            <span className="ff-zh-serif tracking-normal opacity-80">发送</span>
          </button>
          {!ratingsSet && (
            <div className="ff-body text-[10px] text-center mt-2" style={{ color: C.inkLight }}>
              Please rate both questions to send
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

interface RatingQuestionProps {
  label: string;
  hz: string;
  value: Rating;
  onChange: (next: Rating) => void;
  firstStarRef?: React.RefObject<HTMLButtonElement>;
}

function RatingQuestion({ label, hz, value, onChange, firstStarRef }: RatingQuestionProps) {
  const groupRef = useRef<HTMLDivElement>(null);

  function handleKey(e: React.KeyboardEvent<HTMLButtonElement>, idx: number) {
    if (e.key === 'ArrowRight' || e.key === 'ArrowUp') {
      e.preventDefault();
      const next = Math.min(5, idx + 1) as Rating;
      onChange(next);
      focusStar(next);
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') {
      e.preventDefault();
      const next = Math.max(1, idx - 1) as Rating;
      onChange(next);
      focusStar(next);
    } else if (e.key >= '1' && e.key <= '5') {
      e.preventDefault();
      const n = Number(e.key) as Rating;
      onChange(n);
      focusStar(n);
    }
  }

  function focusStar(n: Rating) {
    const btn = groupRef.current?.querySelector<HTMLButtonElement>(`[data-star="${n}"]`);
    btn?.focus();
  }

  return (
    <div>
      <div className="mb-2">
        <div className="ff-body text-[13px] font-semibold" style={{ color: C.ink }}>
          {label}
        </div>
        <div className="ff-zh-serif text-[12px] opacity-60" style={{ color: C.ink }}>
          {hz}
        </div>
      </div>
      <div
        ref={groupRef}
        role="radiogroup"
        aria-label={label}
        className="inline-flex items-center gap-1.5"
      >
        {[1, 2, 3, 4, 5].map((n) => {
          const filled = n <= value;
          const isFocusTarget = value === 0 ? n === 1 : n === value;
          return (
            <button
              key={n}
              ref={n === 1 && firstStarRef ? firstStarRef : undefined}
              type="button"
              role="radio"
              aria-checked={value === n}
              data-star={n}
              tabIndex={isFocusTarget ? 0 : -1}
              onClick={() => onChange(n as Rating)}
              onKeyDown={(e) => handleKey(e, n)}
              className="p-1.5 rounded-full transition-transform active:scale-90"
              aria-label={`${n} star${n === 1 ? '' : 's'}`}
            >
              <Star
                size={28}
                strokeWidth={1.5}
                fill={filled ? C.gold : 'none'}
                color={filled ? C.gold : C.inkSofter}
                aria-hidden
              />
            </button>
          );
        })}
      </div>
    </div>
  );
}
